import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  ErrorText,
  Input,
  MILESTONE_NAME_MAX,
  ROOM_ITEM_VISIBILITIES,
  RowMenu,
  VISIBILITY_LABELS,
  formatDateOnly,
  useToast,
  type RoomItemVisibility,
  type RoomMilestone,
  useSingleSubmit,
} from '@dataroom/shared';
import {
  useCreateMilestone,
  useDeleteMilestone,
  useMilestones,
  useUpdateMilestone,
} from '@/api/hooks';
import { apiMessage } from '@/lib/errors';
import { Modal } from './Modal';

const fieldClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/**
 * TIMELINE MILESTONES (ADR-0047): the dates nothing else in the room records —
 * a management presentation, a target signing. They join the phase release,
 * LOI, exclusivity and closing dates on the weekly digest's timeline. Real dates
 * a person entered; nothing is projected.
 */
export function MilestonesCard({ roomId, readOnly }: { roomId: string; readOnly: boolean }) {
  const milestones = useMilestones(roomId);
  const create = useCreateMilestone(roomId);
  const update = useUpdateMilestone(roomId);
  const remove = useDeleteMilestone(roomId);
  const toast = useToast();
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [visibility, setVisibility] = useState<RoomItemVisibility>('deal_team');
  const [editing, setEditing] = useState<RoomMilestone | null>(null);
  const [deleting, setDeleting] = useState<RoomMilestone | null>(null);

  const canAdd = name.trim().length > 0 && date.length > 0 && !create.isPending;
  const list = milestones.data ?? [];

  // One milestone per click, even on a quick double-click (full-deal test 2026-10-08).
  const beginSubmit = useSingleSubmit();
  function add() {
    if (!canAdd || !beginSubmit(`${name.trim()}|${date}|${visibility}`)) return;
    create.mutate(
      { name: name.trim(), date, visibility },
      {
        onSuccess: (m) => {
          setName('');
          setDate('');
          toast({ message: `Added “${m.name}” to the timeline` });
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Timeline milestones</CardTitle>
        <p className="text-sm text-muted-foreground">
          Dates the room doesn&apos;t already know — a management presentation, a target signing.
          They appear on the weekly digest&apos;s timeline next to the phase, LOI, exclusivity and
          closing dates.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {milestones.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : milestones.error ? (
          <ErrorText error={milestones.error} />
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">No milestones yet.</p>
        ) : (
          <ul className="divide-y rounded-md border" aria-label="Timeline milestones">
            {list.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateOnly(m.date)}
                    {m.visibility ? ` · ${VISIBILITY_LABELS[m.visibility]}` : ''}
                  </p>
                </div>
                {readOnly ? null : (
                  <RowMenu
                    label={m.name}
                    items={[
                      { label: 'Edit…', icon: 'calendar', onSelect: () => setEditing(m) },
                      {
                        label: 'Delete',
                        icon: 'trash',
                        danger: true,
                        onSelect: () => setDeleting(m),
                      },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {readOnly ? null : (
          <form
            className="space-y-3 rounded-md border border-dashed p-4"
            aria-label="Add a milestone"
            onSubmit={(e) => {
              e.preventDefault();
              add();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.4fr)]">
              <div className="space-y-1.5">
                <label htmlFor="milestone-name" className="text-sm font-medium">
                  Milestone
                </label>
                <Input
                  id="milestone-name"
                  placeholder="e.g. Management presentation"
                  value={name}
                  maxLength={MILESTONE_NAME_MAX}
                  onChange={(e) => setName(e.target.value)}
                  disabled={create.isPending}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="milestone-date" className="text-sm font-medium">
                  Date
                </label>
                <input
                  id="milestone-date"
                  type="date"
                  className={fieldClass}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={create.isPending}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="milestone-visibility" className="text-sm font-medium">
                  Who can see it
                </label>
                <select
                  id="milestone-visibility"
                  className={fieldClass}
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as RoomItemVisibility)}
                  disabled={create.isPending}
                >
                  {ROOM_ITEM_VISIBILITIES.map((v) => (
                    <option key={v} value={v}>
                      {VISIBILITY_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" size="sm" disabled={!canAdd}>
                {create.isPending ? 'Adding…' : 'Add milestone'}
              </Button>
              {create.error ? (
                <p className="text-sm text-destructive">{apiMessage(create.error)}</p>
              ) : null}
            </div>
          </form>
        )}
      </CardContent>

      {editing ? (
        <EditMilestoneDialog
          milestone={editing}
          pending={update.isPending}
          error={apiMessage(update.error)}
          onClose={() => {
            update.reset();
            setEditing(null);
          }}
          onSave={(body) =>
            update.mutate(
              { milestoneId: editing.id, body },
              {
                onSuccess: (m) => {
                  setEditing(null);
                  toast({ message: `Saved “${m.name}”` });
                },
              },
            )
          }
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          title="Delete this milestone?"
          confirmLabel="Delete milestone"
          danger
          pending={remove.isPending}
          onCancel={() => {
            remove.reset();
            setDeleting(null);
          }}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast({ message: `Deleted “${deleting.name}”` });
                setDeleting(null);
              },
            })
          }
        >
          <p>
            “{deleting.name}” ({formatDateOnly(deleting.date)}) comes off the timeline and out of
            the next weekly digest.
          </p>
          {remove.error ? <p className="text-destructive">{apiMessage(remove.error)}</p> : null}
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}

function EditMilestoneDialog({
  milestone,
  pending,
  error,
  onClose,
  onSave,
}: {
  milestone: RoomMilestone;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (body: { name?: string; date?: string; visibility?: RoomItemVisibility }) => void;
}) {
  const [name, setName] = useState(milestone.name);
  const [date, setDate] = useState(milestone.date);
  const [visibility, setVisibility] = useState<RoomItemVisibility>(
    milestone.visibility ?? 'deal_team',
  );
  // Only what changed goes to the API.
  const body = {
    ...(name.trim() !== milestone.name ? { name: name.trim() } : {}),
    ...(date !== milestone.date ? { date } : {}),
    ...(visibility !== (milestone.visibility ?? 'deal_team') ? { visibility } : {}),
  };
  const canSave = name.trim().length > 0 && date.length > 0 && Object.keys(body).length > 0;

  return (
    <Modal
      title="Edit milestone"
      onClose={onClose}
      footer={
        <>
          {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button size="sm" disabled={!canSave || pending} onClick={() => onSave(body)}>
              {pending ? 'Saving…' : 'Save milestone'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="edit-milestone-date" className="text-sm font-medium">
            Date
          </label>
          <input
            id="edit-milestone-date"
            type="date"
            className={fieldClass}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={pending}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="edit-milestone-name" className="text-sm font-medium">
            Milestone
          </label>
          <Input
            id="edit-milestone-name"
            value={name}
            maxLength={MILESTONE_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            disabled={pending}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="edit-milestone-visibility" className="text-sm font-medium">
            Who can see it
          </label>
          <select
            id="edit-milestone-visibility"
            className={fieldClass}
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as RoomItemVisibility)}
            disabled={pending}
          >
            {ROOM_ITEM_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {VISIBILITY_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
